import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { PendingIcon } from "@images/svgIcons/pendingIcon"
import {
  BoxStaffPermissionsModal,
  BoxStaffPermissionsModalRef,
} from "@modules/boxStaff/components/boxStaffPermissionsModal"
import { displayIcon, displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { useBoxStaff } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuAvatarCardSidebar } from "@modules/ui/components/huAvatarCardSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { Skeleton } from "primereact/skeleton"
import { useRef } from "react"
import { useTheme } from "styled-components"

export const BoxStaffDetailsPending: React.FC<{
  staffId: string
}> = ({ staffId }) => {
  const { data: staffDetailed, isLoading, deleteBoxStaff, isPending } = useBoxStaff(staffId)
  const theme = useTheme()
  const toast = useToast()
  const { hideSidebar } = useSidebar()
  const boxStaffPermissionsModal = useRef<BoxStaffPermissionsModalRef>(null)
  const handlePermessions = async () => {
    boxStaffPermissionsModal.current?.open()
  }

  const handleCancelInvitation = async () => {
    await deleteBoxStaff()
      .then(() => {
        toast.show({ severity: "success", content: word("box.staff.detailsPending.toast.success") })
        hideSidebar()
      })
      .catch(() => {
        toast.show({ severity: "error", content: word("box.staff.detailsPending.toast.error") })
      })
  }

  return (
    <>
      {/* HEADER */}
      <HuAvatarCardSidebar
        id={staffDetailed?.userId}
        avatar={staffDetailed?.publicProfile?.avatar}
        firstname={staffDetailed?.firstname}
        lastname={staffDetailed?.lastname}
        username={staffDetailed?.username}
        prefixUsername={staffDetailed?.username ? "@" : undefined}
        nationality={staffDetailed?.publicProfile?.nationality}
        email={staffDetailed?.email}
        isLoading={isLoading}
        logo={displayIcon(theme.mainWhite, 50, staffDetailed?.role)}
      />
      <div className="flex flex-column w-full gap-5 mt-4">
        {/* HEADER */}
        <div className="flex align-items-center justify-content-center gap-2">
          <PendingIcon
            color={theme.mainWhite}
            style={{
              width: "24px",
              height: "24px",
            }}
          />
          <HUText fontStyle="TL" color={theme.mainWhite} className="text-center">
            {word("box.staff.detailsPending.header.title")}
          </HUText>
        </div>
        {/* DESCRIPTION */}
        {isLoading ? (
          <Skeleton height="3rem" borderRadius="8px" />
        ) : (
          <HUText fontStyle="BM" color={theme.neutralColor400}>
            {word("box.staff.detailsPending.header.subtitle", {
              role: staffDetailed?.role ? displayRole(staffDetailed.role) : "",
              firstname: staffDetailed?.firstname,
              lastname: staffDetailed?.lastname,
            })}
          </HUText>
        )}
        {/* DATE */}
        <div className="flex flex-row gap-2">
          <div className="flex flex-column gap-1 mt-1">
            <HUText className="uppercase" fontStyle="LS" color={theme.neutralColor400}>
              {"Date de l'invitation"}
            </HUText>
            {isLoading ? (
              <Skeleton height="2rem" borderRadius="8px" />
            ) : (
              <HUText fontStyle="TM">{formatDateWithHyphen(dayjs(staffDetailed?.creationDate).format("L"))}</HUText>
            )}
          </div>
        </div>
        {/* PERMISSIONS  */}
        <div className="flex flex-column mt-4">
          <HuPermissions
            title={word("box.staff.table.dayToDay.permissions.title")}
            role={staffDetailed?.role}
            button={
              <HUButton
                type="Right_Icon"
                size="M"
                colorType="Underline"
                text={word("box.staff.table.dayToDay.button.seeDetails")}
                onClick={() => handlePermessions()}
                className="ml-auto"
                icon={{
                  iconView: <ArrowIcon color={theme.mainWhite} width={14} height={14} arrowDirection={"right"} />,
                }}
              />
            }
          />
        </div>
        <div className="mb-4 flex flex-row gap-2 align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType="Quaternary"
            text={word("box.staff.detailsPending.button.cancel")}
            onClick={() => handleCancelInvitation()}
            disabled={isPending || isLoading}
            className="w-full"
          />
        </div>
      </div>
      <BoxStaffPermissionsModal ref={boxStaffPermissionsModal} />
    </>
  )
}
